import { useEffect, useRef, useState } from 'react';

interface UseAdaptiveDebounceOptions {
    baseDelay?: number;
    fastTypingDelay?: number;
    typingThreshold?: number;
}

export function useAdaptiveDebounce<T>(value: T, options: UseAdaptiveDebounceOptions = {}): T {
    const { baseDelay = 500, fastTypingDelay = 800, typingThreshold = 200 } = options;

    const [debouncedValue, setDebouncedValue] = useState(value);
    const lastTypingTime = useRef<number>(Date.now());
    const isTyping = useRef<boolean>(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const now = Date.now();
        const timeSinceLastTyping = now - lastTypingTime.current;

        // Update last typing time
        lastTypingTime.current = now;
        isTyping.current = true;

        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Determine delay based on typing speed
        const delay = timeSinceLastTyping < typingThreshold ? fastTypingDelay : baseDelay;

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
            setDebouncedValue(value);
            isTyping.current = false;
        }, delay);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [value, baseDelay, fastTypingDelay, typingThreshold]);

    return debouncedValue;
}
